// Status stacking rules (pure, Phaser-free; tests/combat-status.test.ts).
// State lives in each enemy's `statuses` map (EnemyApi.statuses) so enemies can read slow/stun.
//
//   burn       stacks ≤ 5, dps = potency × stacks (× burnPower in the damage system), ticks 4×/s;
//              reaching 5 stacks applies `overheated` for 2 s
//   shock      stacks ≤ 3; reaching 3 discharges into a 0.6 s stun (one stack stays, 2 s lockout
//              before shock can stun again); shocked targets take +15% electric damage
//   slow       potency = speed reduction fraction (strongest wins)
//   vulnerable potency = bonus damage fraction (default +25%)
//   stun       duration only
//   overheated duration only (GRAVITY_FURNACE: overheated enemies explode on death)
// Bosses shrug off most crowd control: stun ×0.35 duration, slow ×0.5 potency.

import type { StatusApplication, StatusId, StatusState } from '../../core/types';

export const BURN_MAX_STACKS = 5;
export const SHOCK_MAX_STACKS = 3;
/** Seconds between DoT ticks (4 ticks per second). */
export const BURN_TICK = 0.25;
export const OVERHEAT_DURATION = 2;
export const SHOCK_STUN_DURATION = 0.6;
export const SHOCK_STUN_LOCKOUT = 2;
export const BOSS_STUN_MULT = 0.35;
export const BOSS_SLOW_MULT = 0.5;
export const MAX_SLOW = 0.8;

export const STATUS_IDS: readonly StatusId[] = ['burn', 'shock', 'slow', 'vulnerable', 'stun', 'overheated'];

/** Defaults when a StatusApplication omits duration / potency. Burn potency is dps per stack. */
export const STATUS_DEFAULTS: Readonly<Record<StatusId, { duration: number; potency: number }>> = {
  burn: { duration: 3, potency: 5 },
  shock: { duration: 2.5, potency: 0 },
  slow: { duration: 1.5, potency: 0.3 },
  vulnerable: { duration: 3, potency: 0.25 },
  stun: { duration: 0.6, potency: 0 },
  overheated: { duration: OVERHEAT_DURATION, potency: 0 },
};

/** Outcome flags of applyStatusRule. */
export const ST_ADDED = 1;
export const ST_IGNITED = 2;
export const ST_OVERHEATED = 4;
export const ST_STUNNED = 8;

function ensure(map: Map<StatusId, StatusState>, id: StatusId): { st: StatusState; added: boolean } {
  let st = map.get(id);
  if (st) return { st, added: false };
  st = { stacks: 0, remaining: 0, potency: 0, tick: 0 };
  map.set(id, st);
  return { st, added: true };
}

function setTimed(map: Map<StatusId, StatusState>, id: StatusId, duration: number): boolean {
  const { st, added } = ensure(map, id);
  st.stacks = 1;
  st.remaining = Math.max(st.remaining, duration);
  return added;
}

/** Apply one status application to a status map. Returns ST_* flags. */
export function applyStatusRule(map: Map<StatusId, StatusState>, s: StatusApplication, isBoss = false): number {
  const def = STATUS_DEFAULTS[s.id];
  if (!def) return 0;
  const duration = s.duration > 0 ? s.duration : def.duration;
  const n = Math.max(1, Math.round(s.stacks ?? 1));
  let ev = 0;

  switch (s.id) {
    case 'burn': {
      const { st, added } = ensure(map, 'burn');
      if (added) ev |= ST_ADDED;
      const before = st.stacks;
      st.stacks = Math.min(BURN_MAX_STACKS, before + n);
      st.remaining = Math.max(st.remaining, duration);
      st.potency = Math.max(st.potency, s.potency ?? def.potency);
      if (before === 0) ev |= ST_IGNITED;
      if (st.stacks >= BURN_MAX_STACKS && !map.has('overheated')) {
        setTimed(map, 'overheated', OVERHEAT_DURATION);
        ev |= ST_OVERHEATED;
      }
      break;
    }
    case 'shock': {
      const { st, added } = ensure(map, 'shock');
      if (added) ev |= ST_ADDED;
      st.remaining = Math.max(st.remaining, duration);
      if (map.has('stun')) {
        // No stacking while stunned: the target stays shocked.
        st.stacks = Math.max(st.stacks, 1);
        break;
      }
      st.stacks = Math.min(SHOCK_MAX_STACKS, st.stacks + n);
      if (st.stacks >= SHOCK_MAX_STACKS && st.tick <= 0) {
        setTimed(map, 'stun', SHOCK_STUN_DURATION * (isBoss ? BOSS_STUN_MULT : 1));
        st.stacks = 1;
        st.tick = SHOCK_STUN_LOCKOUT; // `tick` doubles as the stun lockout for shock
        ev |= ST_STUNNED;
      }
      break;
    }
    case 'slow': {
      const { st, added } = ensure(map, 'slow');
      if (added) ev |= ST_ADDED;
      const p = Math.min(MAX_SLOW, Math.max(0, (s.potency ?? def.potency) * (isBoss ? BOSS_SLOW_MULT : 1)));
      st.stacks = 1;
      st.potency = Math.max(st.potency, p);
      st.remaining = Math.max(st.remaining, duration);
      break;
    }
    case 'vulnerable': {
      const { st, added } = ensure(map, 'vulnerable');
      if (added) ev |= ST_ADDED;
      st.stacks = 1;
      st.potency = Math.max(st.potency, s.potency ?? def.potency);
      st.remaining = Math.max(st.remaining, duration);
      break;
    }
    case 'stun': {
      if (setTimed(map, 'stun', duration * (isBoss ? BOSS_STUN_MULT : 1))) ev |= ST_ADDED;
      ev |= ST_STUNNED;
      break;
    }
    case 'overheated': {
      if (setTimed(map, 'overheated', duration)) ev |= ST_ADDED;
      break;
    }
  }
  return ev;
}

/**
 * Advance every status timer by dt, removing expired entries.
 * Returns the burn damage due this step (sum of the 0.25 s ticks that elapsed, before burnPower).
 */
export function advanceStatuses(map: Map<StatusId, StatusState>, dt: number): number {
  let burn = 0;
  for (let i = 0; i < STATUS_IDS.length; i++) {
    const id = STATUS_IDS[i];
    const st = map.get(id);
    if (!st) continue;
    if (id === 'burn') {
      st.tick += dt;
      while (st.tick >= BURN_TICK) {
        st.tick -= BURN_TICK;
        burn += st.potency * st.stacks * BURN_TICK;
      }
    } else if (id === 'shock' && st.tick > 0) {
      st.tick = Math.max(0, st.tick - dt);
    }
    st.remaining -= dt;
    if (st.remaining <= 0) map.delete(id);
  }
  return burn;
}
